import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { SITE_URL, useSeo } from "@/hooks/useSeo";
import { aboutImage, contactChannels, contentPrinciples, siteInfo, siteStats, testimonials } from "@/mocks/site";

export default function About() {
  useSeo({
    title: "关于我们 - 131吃瓜的定位、内容原则与团队介绍",
    description:
      "了解131吃瓜是谁：一个认真整理梗图、表情包与网络热梗的内容站点。这里说明我们的网站定位、内容原则、用户口碑与联系方式。",
    keywords: "关于131吃瓜,网站介绍,内容原则,梗图站,表情包站",
    path: "/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          "@id": `${SITE_URL}/about#aboutpage`,
          url: `${SITE_URL}/about`,
          name: "关于131吃瓜",
          description: "131吃瓜的网站定位、内容原则与联系方式。",
          inLanguage: "zh-CN",
          isPartOf: { "@id": `${SITE_URL}/#website` },
        },
        {
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`,
          name: siteInfo.name,
          url: `${SITE_URL}/`,
          email: siteInfo.email,
          description: siteInfo.intro,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_URL}/about` },
          ],
        },
      ],
    },
  });

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-10 md:px-8 md:py-14">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <h1 className="mt-6 font-heading text-3xl text-foreground-950 md:text-5xl">关于131吃瓜</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-lg">
            {siteInfo.intro}
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto grid w-full grid-cols-1 items-center gap-10 px-4 py-14 md:px-8 md:py-20 lg:grid-cols-2 lg:gap-16">
          <div className="w-full overflow-hidden rounded-lg border border-background-200">
            <div className="w-full overflow-hidden" style={{ aspectRatio: "3 / 2" }}>
              <img
                src={aboutImage.src}
                alt={aboutImage.alt}
                title="131吃瓜内容团队 · 关于我们"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div>
            <span className="text-xs tracking-[0.2em] text-primary-600">OUR&nbsp;STORY</span>
            <h2 className="mt-2 font-heading text-3xl text-foreground-950 md:text-4xl">网站定位</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              131吃瓜从 {siteInfo.foundedYear} 年开始做内容整理，最开始只是几个人在群里互相分享好笑的图，后来发现「找图」这件事本身就很麻烦，于是干脆把素材收拢到一个地方，按分类和场景整理好。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              我们服务的是喜欢用图说话的人：想在群里接住热梗的普通网友、需要表情包素材的创作者，以及单纯想找点乐子的吃瓜群众。站点不追求花哨，只希望你想用图的时候，能三步之内找到合适的那一张。
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {siteStats.map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <p className="font-heading text-xl text-primary-600">{stat.value}</p>
                  <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100">
        <div className="mx-auto w-full px-4 py-14 md:px-8 md:py-20">
          <div className="max-w-2xl">
            <span className="text-xs tracking-[0.2em] text-accent-700">PRINCIPLES</span>
            <h2 className="mt-2 font-heading text-3xl text-foreground-950 md:text-4xl">内容原则</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              整理素材看起来简单，但要长期做得让人放心，还是得有几条自己的规矩。
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {contentPrinciples.map((item) => (
              <div key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div>
                  <h3 className="font-heading text-lg text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-secondary-50">
        <div className="mx-auto w-full px-4 py-14 md:px-8 md:py-20">
          <div className="max-w-2xl">
            <span className="text-xs tracking-[0.2em] text-secondary-800">VOICES</span>
            <h2 className="mt-2 font-heading text-3xl text-foreground-950 md:text-4xl">用户怎么说</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
              下面是部分网友在留言和邮件里提到我们的话，原话收录，只做了标点整理。
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {testimonials.map((item) => (
              <figure key={item.name} className="rounded-lg border border-secondary-200 bg-background-50 p-6">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-double-quotes-l text-lg"></i>
                </span>
                <blockquote className="mt-4 text-sm leading-relaxed text-foreground-800">{item.text}</blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                    <i className="ri-user-smile-line text-lg"></i>
                  </span>
                  <span>
                    <span className="block text-sm text-foreground-900">{item.name}</span>
                    <span className="block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto w-full px-4 py-14 md:px-8 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
            <div>
              <span className="text-xs tracking-[0.2em] text-primary-600">CONTACT</span>
              <h2 className="mt-2 font-heading text-3xl text-foreground-950 md:text-4xl">怎么找到我们</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                合作、投稿、纠错或授权咨询都可以直接发邮件，也可以到联系页面提交表单，说明清楚来意我们会更快回复。
              </p>
              <Link
                to="/contact"
                className="mt-8 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                前往联系页面
              </Link>
            </div>
            <ul className="space-y-4">
              {contactChannels.map((channel) => (
                <li key={channel.label} className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${channel.icon} text-xl`}></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-base text-foreground-950">{channel.label}</h3>
                    <p className="mt-1 text-sm text-primary-700">{channel.value}</p>
                    <p className="mt-2 text-xs leading-relaxed text-foreground-600">{channel.note}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}